import { POWER_READING } from "@tether-web-portal/components/property-graph/GraphUtils";
import { Circuit, CircuitWithReading } from "./Circuit";
import { ActiveTenantSummary, TenantBlocked } from "./PropertyTenant";
import { GraphColor } from "./Room";

export interface CircuitNode {
  id: string;
  propertyId: string;
  type: CircuitNodeType;
  tagIds: string[];
  parentId: string | null;
  color: GraphColor;
  name: string;
  children: CircuitNode[];
  circuits: CircuitWithReading[];
  createdAt: string;
  /** Additive field on circuit-node-detail responses (Phase 1c). */
  activeTenant?: ActiveTenantSummary | null;
  /**
   * Additive field: set when an ancestor/descendant node holds the tenant, so
   * this node is not assignable. When set, `activeTenant` is null.
   */
  tenantBlocked?: TenantBlocked | null;
}

export interface CircuitNodeWithUsage extends CircuitNode {
  usage: POWER_READING | null;
  children: CircuitNodeWithUsage[];
}

export interface CircuitNodeWithData extends CircuitNode {
  readings: POWER_READING[];
  readingsStatus?: "OK" | "PARTIAL" | "NONE";
  readingsStatusReason?: string;
  circuits: Circuit[];
}

export const CircuitNodeTypes = ["MAINS", "BOARD", "CIRCUIT", "LOAD", "OTHER"] as const;
export type CircuitNodeType = typeof CircuitNodeTypes[number];
export const CircuitNodeTypeDescription: Record<CircuitNodeType, string> = {
  ["MAINS"]: "Mains",
  ["BOARD"]: "Distribution Board",
  ["LOAD"]: "Asset",
  ["CIRCUIT"]: "Circuit",
  ["OTHER"]: "Other",
};

export const getCircuitNodeFromTree = (
  circuitNodeTree: CircuitNode[],
  circuitNodeId: string
): CircuitNode | undefined => {
  if (!circuitNodeId) return undefined;
  if (!circuitNodeTree) return undefined;
  const circuitNode = circuitNodeTree?.find((circuitNode) => circuitNode.id === circuitNodeId);
  if (circuitNode) {
    return circuitNode;
  }
  for (const circuitNode of circuitNodeTree) {
    const childCircuitNode = getCircuitNodeFromTree(circuitNode.children, circuitNodeId);
    if (childCircuitNode) {
      return childCircuitNode;
    }
  }
  return undefined;
};
